import React, { useState } from 'react';
import styled from 'styled-components';
import { FlexView, COLORS } from '@cuemath/comps';
import { Button, Text } from '@cuemath/web-comps';

import { useLearnosityItemUpdatePatch } from 'actions/learnosity-item-update';

import { ReactComponent as HelpIcon } from 'img/left-nav/help.svg';

import { getCookie } from 'utils';
import { getAllLearnosityItems } from 'utils/learnosity';

const Wrapper = styled(FlexView)`
  margin: 40px auto 0;
  gap: 36px;
  padding: 24px;
`;

const InputContainer = styled(FlexView)`
  min-width: fit-content;
  position: relative;
  gap: 24px;
`;

const Tooltip = styled(FlexView)`
  position: absolute;
  left: 0;
  top: -16px;
`;

const TooltipText = styled.div`
  position: absolute;
  left: 28px;
  top: -12px;
  background-color: ${COLORS.COLA};
  border-radius: 4px;
  padding: 8px;
  width: 320px;
  color: ${COLORS.WHITE};
`;

const HelpLogo = styled(HelpIcon)`
  width: 24px;
  height: 24px;
  cursor: pointer;
`;

const TextArea = styled.textarea`
  width: 524px;
  min-height: 224px;
  margin-top: 10px;
  outline: none;
  border-radius: 8px;
  border: 1px solid ${COLORS.GRAY_XLIGHT};
  padding: 12px;
  resize: none;
`;

const ButtonWrapper = styled(Button)`
  height: 40px;
`;

const FailedItemsList = styled(FlexView)`
  gap: 8px;
  position: relative;
  top: -60px;
`;

const FailedItemsBox = styled(FlexView)`
  width: 620px;
  height: 420px;
  border: 1px solid ${COLORS.GRAY_XLIGHT};
  border-radius: 8px;
  padding: 12px;
  overflow-y: auto;
`;

const GridBox = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 50px;
`;

const LearnosityItemUpdated = () => {
  const [learnosityItems, setLearnosityItems] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [patchAPIFail, setPatchAPIFail] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [patchAPIData, setPatchAPIData] = useState(null);

  const { failed_questions: failedQuestions } = patchAPIData || {};
  const { item_refs: failedItemRefs, reason: reasonForNotUpdating } =
    failedQuestions || {};

  const onItemUpdatePost = (errorMessage, data) => {
    if (errorMessage) {
      setPatchAPIFail(true);
      setErrorMessage(errorMessage);
    } else {
      setPatchAPIData(data);
      setErrorMessage('Saved data.');
    }
  };

  const { patch: patchLearnosityItems, isProcessing } = useLearnosityItemUpdatePatch({
    onComplete: onItemUpdatePost,
  });

  const clearFailStates = () => {
    setPatchAPIFail(false);
    setErrorMessage('');
  };

  const addFailState = message => {
    setPatchAPIFail(true);
    setErrorMessage(message || '');
  };

  const onTextAreaChange = e => {
    const { value } = e?.target || {};

    setLearnosityItems(value);
    clearFailStates();
  };

  const submitLearnosityItems = () => {
    const allItems = getAllLearnosityItems(learnosityItems);
    const userId = getCookie('user_id') || getCookie('emp_id') || 'learnosity_items_id';

    if (!allItems || allItems.length > 100) {
      const failStateMessage =
        allItems.length > 100 ? `You cannot update more than 100 items at once.` : '';

      addFailState(failStateMessage);
    } else {
      patchLearnosityItems(userId, {
        item_references: allItems,
      });
    }
  };

  return (
    <Wrapper justifyContent="center" alignItems="flex-start">
      <Text variant="h4">Learnosity Item Updated</Text>

      <GridBox>
        <InputContainer alignItems="flex-start">
          <Tooltip>
            <HelpLogo
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            />
            {showTooltip && (
              <TooltipText>
                Please make sure learnosity items are line separated.
              </TooltipText>
            )}
          </Tooltip>

          <TextArea
            rows={5}
            onChange={onTextAreaChange}
            placeholder="Please make sure learnosity items are line separated."
          />

          {patchAPIFail && (
            <Text color={COLORS.RED_SHADE}>
              {errorMessage ||
                `Please make sure learnosity items are line separated and your email id is valid.`}
            </Text>
          )}

          {!patchAPIFail && errorMessage && (
            <Text color={COLORS.BRIGHT_GREEN}>{errorMessage}</Text>
          )}

          <ButtonWrapper
            isBusy={isProcessing}
            label="Submit"
            onClick={submitLearnosityItems}
          />
        </InputContainer>

        {failedQuestions && !!failedItemRefs?.length && (
          <FailedItemsList>
            <Text variant="h4">Items failed to update.</Text>

            <Text variant="eyebrow1" color={COLORS.RED_SHADE}>
              {reasonForNotUpdating}
            </Text>

            <FailedItemsBox>
              {failedItemRefs?.map(itemRef => {
                return <li key={itemRef}>{itemRef}</li>;
              })}
            </FailedItemsBox>
          </FailedItemsList>
        )}
      </GridBox>
    </Wrapper>
  );
};

export default LearnosityItemUpdated;
